// Presents the expanded, status-tabbed Store Action view with category and text filtering.
import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  makeStyles,
  Tab,
  Tabs,
  TextField,
} from '@rentacenter/racstrap';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  CompactStoreActionCategory,
  StoreAction,
  StoreActionPage,
} from './storeActionDomain';
import { getStoreActionMessage } from './storeActionLocalization';
import {
  composeExpandedPages,
  useStoreActionActiveListQuery,
  useStoreActionCompletedListQuery,
  useStoreActionPriceTagQueueQuery,
} from './storeActionQueries';
import { composePriceTagQueueAction } from './storeActionPriceTag';
import { StoreActionRow } from './StoreActionRow';
import { StoreActionPagination } from './StoreActionPagination';
import { canManageCompletedStoreActions } from './storeActionPermissions';
import {
  filterExpandedStoreActionItems,
  formatStoreActionCount,
  getCompactCategoryCounts,
} from './storeActionFilters';
import { useStoreActionCategoryFilter } from './useStoreActionCategoryFilter';

type StoreActionExpandedDialogProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onDssActivate: (item: StoreAction, storeNumber: string) => void;
  readonly onTransferActivate?: (
    item: StoreAction,
    type: 'EAITF' | 'EAITT',
    trigger: HTMLElement
  ) => void;
  readonly role: string;
  readonly storeNumber: string;
};

type ExpandedStatus = 'active' | 'completed';

const CATEGORY_LABEL_KEYS: Readonly<
  Record<
    CompactStoreActionCategory,
    Parameters<typeof getStoreActionMessage>[0]
  >
> = {
  ALL: 'categoryAll',
  AGR: 'categoryAgreement',
  INV: 'categoryInventory',
  CUS: 'categoryCustomer',
  FACTPAY: 'categoryInvoice',
  OTHER: 'categoryOther',
};

const ACTIVE_BLUE = '#2179fe';
const PANEL_BORDER = '1px solid #e2e8f0';
const STATUS_ACTIVE = '#ff5a1f';
const CONTROL_BORDER = '#d5dce6';
const UI_FONT = 'Open Sans, sans-serif';

const useStyles = makeStyles({
  paper: {
    backgroundColor: '#fff',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 64px)',
    maxHeight: 'calc(100% - 32px)',
    minHeight: 0,
  },
  title: {
    alignItems: 'center',
    borderBottom: PANEL_BORDER,
    color: '#212529',
    display: 'flex',
    fontFamily: UI_FONT,
    fontSize: 16,
    fontWeight: 700,
    justifyContent: 'space-between',
    padding: '16px 28px',
    position: 'relative',
  },
  close: {
    alignItems: 'center',
    background: '#f1f7ff',
    border: `1px solid ${ACTIVE_BLUE}`,
    borderRadius: 4,
    color: ACTIVE_BLUE,
    display: 'inline-flex',
    justifyContent: 'center',
    minHeight: 28,
    minWidth: 28,
    padding: 3,
    position: 'absolute',
    right: 28,
    top: '50%',
    transform: 'translateY(-50%)',
    '&:hover': {
      backgroundColor: '#e6f0ff',
    },
  },
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    padding: '0 28px 20px',
  },
  statusTabs: {
    borderBottom: PANEL_BORDER,
    overflowX: 'auto',
    '& .MuiTab-root': {
      color: '#6b7280',
      fontFamily: UI_FONT,
      fontSize: 12,
      fontWeight: 600,
      minHeight: 40,
      minWidth: 'auto',
      padding: '8px 10px',
    },
    '& .Mui-selected': {
      color: STATUS_ACTIVE,
    },
    '& .MuiTabs-indicator': {
      backgroundColor: STATUS_ACTIVE,
      height: 2,
    },
  },
  categoryPills: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  categoryPill: {
    backgroundColor: '#fff',
    border: `1px solid ${CONTROL_BORDER}`,
    borderRadius: 4,
    color: '#475569',
    cursor: 'pointer',
    fontFamily: UI_FONT,
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1.2,
    padding: '4px 8px',
  },
  categoryPillSelected: {
    backgroundColor: ACTIVE_BLUE,
    borderColor: ACTIVE_BLUE,
    color: '#fff',
  },
  filterBar: {
    alignItems: 'center',
    borderBottom: PANEL_BORDER,
    display: 'flex',
    gap: 16,
    marginTop: 16,
    paddingBottom: 14,
    '@media (max-width: 720px)': {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  searchField: {
    flex: '0 1 500px',
    marginLeft: 'auto',
    '& .MuiOutlinedInput-root': {
      fontFamily: UI_FONT,
      fontSize: 12,
      height: 34,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: CONTROL_BORDER,
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#94a3b8',
      opacity: 1,
    },
    '@media (max-width: 720px)': {
      flexBasis: 'auto',
      marginLeft: 0,
    },
  },
  clearSearch: {
    background: 'transparent',
    border: 0,
    color: '#1769aa',
    cursor: 'pointer',
    padding: 4,
  },
  panel: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    minHeight: 160,
  },
  statusPanel: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    '&[hidden]': {
      display: 'none',
    },
  },
  table: {
    border: `1px solid ${CONTROL_BORDER}`,
    borderRadius: 6,
    display: 'grid',
    gridTemplateColumns: '13% 13% minmax(280px, 40%) 17% minmax(100px, 17%)',
    minWidth: 760,
    overflow: 'hidden',
  },
  tableScroll: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  },
  headerRow: {
    backgroundColor: '#f8fafc',
    color: '#212529',
    display: 'contents',
    fontFamily: UI_FONT,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    '& > *': {
      borderBottom: `1px solid ${CONTROL_BORDER}`,
      padding: '8px 12px',
    },
  },
  tableRow: {
    display: 'contents',
    '& > *': {
      alignItems: 'center',
      borderBottom: '1px solid #f1f5f9',
      display: 'flex',
      fontFamily: UI_FONT,
      fontSize: '0.8rem',
      minWidth: 0,
      padding: '12px 12px',
    },
    '&[tabindex="0"] > *': {
      cursor: 'pointer',
    },
    '&[tabindex="0"]:hover > *': {
      backgroundColor: '#eef4fb',
    },
    '& [role="cell"]:nth-child(3)': {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    '& button': {
      background: 'transparent',
      border: 0,
      color: '#1769aa',
      cursor: 'pointer',
      font: 'inherit',
      fontWeight: 600,
      padding: 0,
    },
    '& button:not(:disabled):hover': {
      backgroundColor: '#dbeafe',
    },
    '& .store-action-row__chip': {
      alignItems: 'center',
      background: 'transparent',
      border: 0,
      borderRadius: 0,
      display: 'inline-flex',
      fontSize: '0.8rem',
      fontWeight: 600,
      lineHeight: 1.2,
      padding: 0,
      whiteSpace: 'nowrap',
      width: 'auto',
    },
  },
});

type StoreActionExpandedSegmentProps = {
  readonly role: string;
  readonly category: CompactStoreActionCategory;
  readonly onCategoryChange: (category: CompactStoreActionCategory) => void;
  readonly searchQuery: string;
  readonly onSearchQueryChange: (searchQuery: string) => void;
  readonly page: StoreActionPage | undefined;
  readonly onLoadMore: () => void;
  readonly isFetchingNextPage: boolean;
  readonly isRefetching: boolean;
  readonly hasNextPage: boolean;
  readonly status: ExpandedStatus;
  readonly onDssActivate: (item: StoreAction, storeNumber: string) => void;
  readonly onTransferActivate?: (
    item: StoreAction,
    type: 'EAITF' | 'EAITT',
    trigger: HTMLElement
  ) => void;
  readonly storeNumber: string;
};

type StoreActionExpandedFilterBarProps = Pick<
  StoreActionExpandedSegmentProps,
  'category' | 'onCategoryChange' | 'searchQuery' | 'onSearchQueryChange'
> & {
  readonly counts: Readonly<Record<CompactStoreActionCategory, number>>;
  readonly categories: ReadonlyArray<CompactStoreActionCategory>;
};

const StoreActionExpandedFilterBar: React.FC<StoreActionExpandedFilterBarProps> =
  ({
    category,
    categories,
    counts,
    onCategoryChange,
    onSearchQueryChange,
    searchQuery,
  }) => {
    const classes = useStyles();

    return (
      <div className={classes.filterBar}>
        <div
          aria-label={getStoreActionMessage('categoryFilter')}
          className={classes.categoryPills}
          role="group"
        >
          {categories.map((bucket) => (
            <button
              aria-pressed={category === bucket}
              className={`${classes.categoryPill} ${
                category === bucket ? classes.categoryPillSelected : ''
              }`}
              key={bucket}
              onClick={() => onCategoryChange(bucket)}
              type="button"
            >
              {`${getStoreActionMessage(
                CATEGORY_LABEL_KEYS[bucket]
              )} (${formatStoreActionCount(counts[bucket])})`}
            </button>
          ))}
        </div>
        <TextField
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon aria-hidden="true" icon={faSearch} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <button
                  aria-label={getStoreActionMessage('clearSearch')}
                  className={classes.clearSearch}
                  onClick={() => onSearchQueryChange('')}
                  type="button"
                >
                  <FontAwesomeIcon aria-hidden="true" icon={faTimes} />
                </button>
              </InputAdornment>
            ) : undefined,
          }}
          inputProps={{
            'aria-label': getStoreActionMessage('searchActionItems'),
          }}
          onChange={(event) => onSearchQueryChange(event.target.value)}
          placeholder={getStoreActionMessage('searchActionItems')}
          value={searchQuery}
          variant="outlined"
        />
      </div>
    );
  };

type StoreActionExpandedResultsProps = Pick<
  StoreActionExpandedSegmentProps,
  | 'hasNextPage'
  | 'isFetchingNextPage'
  | 'isRefetching'
  | 'onLoadMore'
  | 'onDssActivate'
  | 'onTransferActivate'
  | 'page'
  | 'role'
  | 'status'
  | 'storeNumber'
> & {
  readonly hasActiveCriteria: boolean;
  readonly visibleItems: ReadonlyArray<StoreActionPage['items'][number]>;
};

const StoreActionExpandedResults: React.FC<StoreActionExpandedResultsProps> = ({
  hasActiveCriteria,
  hasNextPage,
  isFetchingNextPage,
  isRefetching,
  onLoadMore,
  page,
  role,
  status,
  onDssActivate,
  onTransferActivate,
  storeNumber,
  visibleItems,
}) => {
  const classes = useStyles();

  if (!page) {
    return (
      <div
        aria-busy="true"
        aria-label={getStoreActionMessage('loading')}
        className={classes.loading}
        role="status"
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div
      aria-busy={isFetchingNextPage || undefined}
      className={classes.tableScroll}
    >
      <div className={classes.table} role="table">
        <div className={classes.headerRow} role="row">
          <div role="columnheader">{getStoreActionMessage('dateCreated')}</div>
          <div role="columnheader">{getStoreActionMessage('type')}</div>
          <div role="columnheader">{getStoreActionMessage('description')}</div>
          <div role="columnheader">{getStoreActionMessage('status')}</div>
          <div role="columnheader">{getStoreActionMessage('actions')}</div>
        </div>
        {visibleItems.map((item) => (
          <StoreActionRow
            className={classes.tableRow}
            item={item}
            key={String(item.storeActionItemId)}
            onDssActivate={onDssActivate}
            onTransferActivate={onTransferActivate}
            presentation="table"
            role={role}
            storeNumber={storeNumber}
          />
        ))}
      </div>
      {page.total === 0 ? (
        <div role="status">
          {getStoreActionMessage(
            status === 'active' ? 'pendingEmpty' : 'completedEmpty'
          )}
        </div>
      ) : hasActiveCriteria && visibleItems.length === 0 ? (
        <div role="status">{getStoreActionMessage('criteriaEmpty')}</div>
      ) : null}
      <StoreActionPagination
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isRefetching={isRefetching}
        onLoadMore={onLoadMore}
      />
    </div>
  );
};

const StoreActionExpandedSegment: React.FC<StoreActionExpandedSegmentProps> = ({
  role,
  category,
  onCategoryChange,
  searchQuery,
  onSearchQueryChange,
  page,
  onLoadMore,
  isFetchingNextPage,
  isRefetching,
  hasNextPage,
  status,
  onDssActivate,
  onTransferActivate,
  storeNumber,
}) => {
  const classes = useStyles();
  const counts = getCompactCategoryCounts(
    page ? page.countsByCategory : {},
    page ? page.total : 0
  );
  const { categories, selectedCategory } = useStoreActionCategoryFilter(
    category,
    onCategoryChange,
    counts
  );
  // Both criteria narrow the loaded page; loading more is required to search additional pages.
  const visibleItems = page
    ? filterExpandedStoreActionItems(page.items, selectedCategory, searchQuery)
    : [];
  const hasActiveCriteria =
    selectedCategory !== 'ALL' || searchQuery.trim().length > 0;

  return (
    <div
      aria-labelledby={`store-action-${status}-tab`}
      className={classes.panel}
      role="tabpanel"
    >
      <StoreActionExpandedFilterBar
        category={selectedCategory}
        categories={categories}
        counts={counts}
        onCategoryChange={onCategoryChange}
        onSearchQueryChange={onSearchQueryChange}
        searchQuery={searchQuery}
      />
      <StoreActionExpandedResults
        role={role}
        hasActiveCriteria={hasActiveCriteria}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isRefetching={isRefetching}
        onLoadMore={onLoadMore}
        onDssActivate={onDssActivate}
        onTransferActivate={onTransferActivate}
        page={page}
        status={status}
        storeNumber={storeNumber}
        visibleItems={visibleItems}
      />
    </div>
  );
};

export const StoreActionExpandedDialog: React.FC<StoreActionExpandedDialogProps> =
  ({
    isOpen,
    onClose,
    onDssActivate,
    onTransferActivate,
    role,
    storeNumber,
  }) => {
    const classes = useStyles();
    const [status, setStatus] = React.useState<ExpandedStatus>('active');
    const canManageCompleted = canManageCompletedStoreActions(role);
    const selectedStatus = canManageCompleted ? status : 'active';
    React.useEffect(() => {
      if (!canManageCompleted) {
        setStatus('active');
      }
    }, [canManageCompleted]);
    const [activeCategory, setActiveCategory] =
      React.useState<CompactStoreActionCategory>('ALL');
    const [completedCategory, setCompletedCategory] =
      React.useState<CompactStoreActionCategory>('ALL');
    const [activeSearchQuery, setActiveSearchQuery] = React.useState('');
    const [completedSearchQuery, setCompletedSearchQuery] = React.useState('');
    const activeQuery = useStoreActionActiveListQuery(
      storeNumber,
      isOpen && selectedStatus === 'active',
      role
    );
    const completedQuery = useStoreActionCompletedListQuery(
      storeNumber,
      isOpen && canManageCompleted && selectedStatus === 'completed',
      role
    );
    const priceTagQuery = useStoreActionPriceTagQueueQuery(storeNumber);
    const activeComposition = composeExpandedPages(
      activeQuery.data ? activeQuery.data.pages : []
    );
    const completedComposition = composeExpandedPages(
      completedQuery.data ? completedQuery.data.pages : []
    );
    const activePage =
      activeComposition.pages.length > 0
        ? composePriceTagQueueAction(
            {
              ...activeComposition.pages[0],
              items: activeComposition.items,
              total: activeComposition.total,
            },
            Boolean(priceTagQuery.data)
          )
        : undefined;
    const completedPage =
      completedComposition.pages.length > 0
        ? {
            ...completedComposition.pages[0],
            items: completedComposition.items,
            total: completedComposition.total,
          }
        : undefined;

    return (
      <Dialog
        aria-labelledby="store-action-expanded-heading"
        classes={{ paper: classes.paper }}
        fullWidth
        maxWidth="xl"
        onClose={onClose}
        open={isOpen}
      >
        <DialogTitle
          className={classes.title}
          id="store-action-expanded-heading"
        >
          <span>{getStoreActionMessage('heading')}</span>
          <Button
            aria-label={getStoreActionMessage('closeExpandedView')}
            className={classes.close}
            onClick={onClose}
            title={getStoreActionMessage('closeExpandedView')}
            type="button"
          >
            <FontAwesomeIcon aria-hidden="true" icon={faTimes} />
          </Button>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <Tabs
            aria-label={getStoreActionMessage('actionStatus')}
            className={classes.statusTabs}
            onChange={(_event, value) =>
              setStatus(
                value === 0 || !canManageCompleted ? 'active' : 'completed'
              )
            }
            value={selectedStatus === 'active' ? 0 : 1}
          >
            <Tab
              aria-controls="store-action-active-panel"
              id="store-action-active-tab"
              label={getStoreActionMessage('toDoItems')}
            />
            {canManageCompleted ? (
              <Tab
                aria-controls="store-action-completed-panel"
                id="store-action-completed-tab"
                label={getStoreActionMessage('completedItems')}
              />
            ) : null}
          </Tabs>
          <div
            className={classes.statusPanel}
            id="store-action-active-panel"
            hidden={selectedStatus !== 'active'}
          >
            <StoreActionExpandedSegment
              role={role}
              category={activeCategory}
              onCategoryChange={setActiveCategory}
              searchQuery={activeSearchQuery}
              onSearchQueryChange={setActiveSearchQuery}
              page={activePage}
              hasNextPage={Boolean(activeQuery.hasNextPage)}
              isFetchingNextPage={activeQuery.isFetchingNextPage}
              isRefetching={activeQuery.isRefetching}
              onDssActivate={onDssActivate}
              onTransferActivate={onTransferActivate}
              onLoadMore={() =>
                activeQuery.fetchNextPage({ cancelRefetch: false })
              }
              status="active"
              storeNumber={storeNumber}
            />
          </div>
          {canManageCompleted ? (
            <div
              className={classes.statusPanel}
              id="store-action-completed-panel"
              hidden={selectedStatus !== 'completed'}
            >
              <StoreActionExpandedSegment
                role={role}
                category={completedCategory}
                onCategoryChange={setCompletedCategory}
                searchQuery={completedSearchQuery}
                onSearchQueryChange={setCompletedSearchQuery}
                page={completedPage}
                hasNextPage={Boolean(completedQuery.hasNextPage)}
                isFetchingNextPage={completedQuery.isFetchingNextPage}
                isRefetching={completedQuery.isRefetching}
                onDssActivate={onDssActivate}
                onTransferActivate={onTransferActivate}
                onLoadMore={() =>
                  completedQuery.fetchNextPage({ cancelRefetch: false })
                }
                status="completed"
                storeNumber={storeNumber}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    );
  };
